// src/pages/ApexDemo/ApexDemo.js

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getApiBaseUrl } from '../config';
import AudioRecorder from '../components/AudioRecorder';
import './ApexDemo.css';

// ─── Audio helpers (local — extracted to utils/audioUtils.js when ready) ──────
const TARGET_SR = 16000;

function encodeWAV(samples) {
  const len = samples.length;
  const buf = new ArrayBuffer(44 + len * 2);
  const view = new DataView(buf);
  const w4 = (o, s) => { for (let i = 0; i < 4; i++) view.setUint8(o + i, s.charCodeAt(i)); };
  w4(0, 'RIFF'); view.setUint32(4, 36 + len * 2, true);
  w4(8, 'WAVE'); w4(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, TARGET_SR, true);
  view.setUint32(28, TARGET_SR * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  w4(36, 'data'); view.setUint32(40, len * 2, true);
  let o = 44;
  for (let i = 0; i < len; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(o, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    o += 2;
  }
  return new Blob([buf], { type: 'audio/wav' });
}

function downsampleChunk(input, hwSR) {
  if (hwSR === TARGET_SR) return input;
  const ratio = hwSR / TARGET_SR;
  const outLen = Math.floor(input.length / ratio);
  const out = new Float32Array(outLen);
  for (let i = 0; i < outLen; i++) {
    const pos = i * ratio;
    const idx = Math.floor(pos);
    const f = pos - idx;
    out[i] = (input[idx] ?? 0) * (1 - f) + (input[idx + 1] ?? 0) * f;
  }
  return out;
}

// ─── Constants ────────────────────────────────────────────────────────────────
const SAMPLE_DURATION_MS = 1800;  // matches Pi record_one_utterance window
const MIN_SAMPLES = 4;
const MAX_SAMPLES = 4;

// ─── Phase 1: Training panel ──────────────────────────────────────────────────
function TrainPhase({ onTrained }) {
  const [keyword, setKeyword] = useState('');
  const [samples, setSamples] = useState([]);   // true = recorded
  const [recordingIdx, setRecordingIdx] = useState(null); // slot currently recording
  const [countdown, setCountdown] = useState(null);
  const [training, setTraining] = useState(false);
  const [trainProgress, setTrainProgress] = useState(0);
  const [error, setError] = useState('');
  const [engineReady, setEngineReady] = useState(false);

  // Guards
  const isRecordingRef = useRef(false);
  const stopRecordRef = useRef(null);
  const countdownRef = useRef(null);

  // On mount: reset any stale backend session from a previous page load,
  // then warm up the engine. This keeps frontend slot state and backend
  // audio_samples in sync whenever the user refreshes.
  // Also pre-prompts for microphone access.
  useEffect(() => {
    // 1. Reset backend session & check status
    fetch(`${getApiBaseUrl()}/api/fsl/reset`, { method: 'POST', credentials: 'include' })
      .catch(() => { })
      .finally(() => {
        fetch(`${getApiBaseUrl()}/api/fsl/status`, { credentials: 'include' })
          .then(r => r.json())
          .then(d => { if (d.status === 'ready') setEngineReady(true); })
          .catch(() => setError('Could not reach backend.'));
      });

    // 2. Pre-prompt for microphone access
    navigator.mediaDevices.getUserMedia({ audio: true })
      .then(stream => {
        // Release the stream immediately so the recording light turns off
        stream.getTracks().forEach(t => t.stop());
      })
      .catch(err => {
        console.warn('Microphone permission denied or unavailable:', err);
      });
  }, []);

  // Set keyword → reset samples
  function handleKeywordChange(e) {
    const val = e.target.value.toUpperCase().replace(/[^A-Z0-9\s]/g, '');
    setKeyword(val);
    setSamples([]);
    setError('');
  }

  // ── Record one sample slot ─────────────────────────────────
  async function recordSlot(idx) {
    if (isRecordingRef.current) return;
    if (!keyword.trim()) { setError('Enter a keyword first.'); return; }

    isRecordingRef.current = true;
    setRecordingIdx(idx);
    setError('');

    let stream, audioCtx, source, processor;
    const chunks = [];
    let hwSR = TARGET_SR;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true }
      });
      audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      source = audioCtx.createMediaStreamSource(stream);
      processor = audioCtx.createScriptProcessor(2048, 1, 1);
      hwSR = audioCtx.sampleRate;

      processor.onaudioprocess = (e) => {
        chunks.push(new Float32Array(e.inputBuffer.getChannelData(0)));
      };
      source.connect(processor);
      processor.connect(audioCtx.destination);

      // Countdown ticker
      let msLeft = SAMPLE_DURATION_MS;
      setCountdown(Math.ceil(msLeft / 1000));
      countdownRef.current = setInterval(() => {
        msLeft -= 250;
        setCountdown(Math.max(0, Math.ceil(msLeft / 1000)));
      }, 250);

      // Return a promise so we can await the full stop
      await new Promise((resolve) => {
        stopRecordRef.current = resolve;
        setTimeout(resolve, SAMPLE_DURATION_MS);
      });

    } finally {
      clearInterval(countdownRef.current);
      setCountdown(null);
      processor?.disconnect();
      source?.disconnect();
      stream?.getTracks().forEach(t => t.stop());

      // Build WAV from accumulated chunks
      const totalLen = chunks.reduce((a, c) => a + c.length, 0);
      if (totalLen > 0) {
        const merged = new Float32Array(totalLen);
        let pos = 0;
        for (const c of chunks) { merged.set(c, pos); pos += c.length; }
        const downsampled = downsampleChunk(merged, audioCtx.sampleRate);
        const wav = encodeWAV(downsampled);

        // Upload sample
        const form = new FormData();
        form.append('audio', wav, 'sample.wav');
        form.append('keyword', keyword.trim());
        try {
          const res = await fetch(`${getApiBaseUrl()}/api/fsl/add-sample`, {
            method: 'POST', body: form, credentials: 'include'
          });
          const data = await res.json();
          if (data.success) {
            setSamples(prev => {
              const next = [...prev];
              next[idx] = true;
              return next;
            });
          } else {
            setError(data.error || 'Failed to upload sample.');
          }
        } catch {
          setError('Upload failed — is the backend running?');
        }
      }

      audioCtx?.close();
      isRecordingRef.current = false;
      stopRecordRef.current = null;
      setRecordingIdx(null);
    }
  }

  function stopEarly() {
    stopRecordRef.current?.();
  }

  // ── Train ──────────────────────────────────────────────────
  async function handleTrain() {
    const recorded = samples.filter(Boolean).length;
    if (recorded < MIN_SAMPLES) {
      setError(`Record at least ${MIN_SAMPLES} samples before training.`);
      return;
    }
    setError('');
    setTraining(true);
    setTrainProgress(10);

    // Animate to 70% while request runs (matches Pi training UX)
    const tick = setInterval(() => {
      setTrainProgress(p => p < 70 ? p + 15 : p);
    }, 400);

    try {
      const res = await fetch(`${getApiBaseUrl()}/api/fsl/calibrate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ threshold: 0.80, boost: 15.0 }),
        credentials: 'include',
      });
      clearInterval(tick);
      const data = await res.json();

      if (data.success) {
        setTrainProgress(100);
        setTimeout(() => onTrained(keyword.trim()), 350);
      } else {
        setError(data.error || 'Training failed.');
        setTraining(false);
        setTrainProgress(0);
      }
    } catch {
      clearInterval(tick);
      setError('Training failed — connection error.');
      setTraining(false);
      setTrainProgress(0);
    }
  }


  // ── Delete sample ──────────────────────────────────────────
  async function deleteSlot(idx) {
    const count = samples.filter(Boolean).length;
    if (count === 0) return;
    try {
      await fetch(`${getApiBaseUrl()}/api/fsl/delete-sample`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ keyword: keyword.trim(), sample_index: count - 1 }),
        credentials: 'include',
      });
      setSamples(prev => {
        const next = [...prev];
        next[idx] = false;
        const active = next.filter(Boolean).length;
        return Array(MAX_SAMPLES).fill(false).map((_, i) => i < active);
      });
    } catch {
      setError('Could not delete sample.');
    }
  }

  const recorded = samples.filter(Boolean).length;
  const canRecord = !!keyword.trim() && engineReady && !training;
  const canTrain = recorded >= MIN_SAMPLES && !training && !isRecordingRef.current;

  return (
    <div className="phase-wrap">
      <div className="phase-header">
        <span className="demo-badge">Step 1 of 2</span>
        <h1>Train your keyword</h1>
        <p>
          Type your domain-specific keyword, then record {MIN_SAMPLES} short
          spoken examples.
        </p>
        {!engineReady && (
          <div className="engine-status loading">
            <span className="spinner-sm" /> Loading model…
          </div>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="train-card demo-card">
        {/* Keyword input */}
        <div className="keyword-input-row">
          <input
            className="keyword-field"
            value={keyword}
            onChange={handleKeywordChange}
            placeholder="E.g. AFFIDAVIT, CRYPTOCURRENCY, SPIROMETRY..."
            disabled={training || recordingIdx !== null}
            maxLength={32}
          />
        </div>

        {/* Sample slots */}
        <div className="slots-section">
          <div className="slots-label">
            Audio samples
            <span className="slots-count">{recorded} / {MAX_SAMPLES}</span>
          </div>
          <div className="slots-row">
            {Array(MAX_SAMPLES).fill(null).map((_, idx) => {
              const isActive = samples[idx] === true;
              const isRecording = recordingIdx === idx;
              const isDisabled = !canRecord || (recordingIdx !== null && !isRecording);

              return (
                <button
                  key={idx}
                  className={[
                    'sample-slot',
                    isActive ? 'active' : '',
                    isRecording ? 'recording' : '',
                  ].filter(Boolean).join(' ')}
                  onClick={() => {
                    if (isRecording) stopEarly();
                    else if (isActive) deleteSlot(idx);
                    else recordSlot(idx);
                  }}
                  disabled={isDisabled && !isRecording}
                  title={
                    isRecording ? `Recording… ${countdown ?? ''}s — click to stop early`
                      : isActive ? 'Recorded ✓ — click to delete'
                        : `Record sample ${idx + 1}`
                  }
                >
                  {isRecording
                    ? (countdown !== null ? countdown : '…')
                    : isActive
                      ? '✓'
                      : idx + 1}
                </button>
              );
            })}
          </div>
          <p className="slots-hint">
            {recordingIdx !== null
              ? `Speak "${keyword || '…'}" clearly now…`
              : recorded === 0
                ? 'Click a slot to record an audio sample of your keyword.'
                : recorded < MIN_SAMPLES
                  ? `Record ${MIN_SAMPLES - recorded} more sample${MIN_SAMPLES - recorded > 1 ? 's' : ''} to enable training.`
                  : 'Ready to train.'}
          </p>
        </div>

        {/* Train button / progress */}
        <div className="train-action">
          {training ? (
            <div className="progress-bar-container">
              <div className="progress-bar-fill" style={{ width: `${trainProgress}%` }} />
              <span className="progress-text">
                Training acoustic model… {trainProgress < 100 ? `${trainProgress}%` : 'Done!'}
              </span>
            </div>
          ) : (
            <button
              className="cta-button primary large w-full"
              onClick={handleTrain}
              disabled={!canTrain}
            >
              Train Model →
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Phase 2: Testing panel ───────────────────────────────────────────────────
function TestPhase({ keyword, onRetrain }) {
  const [testMode, setTestMode] = useState('detection'); // 'detection' | 'transcription'
  const [log, setLog] = useState([]);   // detection history
  const [latest, setLatest] = useState(null); // most recent result
  const [isListening, setIsListening] = useState(false);

  const MAX_LOG = 8;

  // Reset state when switching between tabs
  useEffect(() => {
    setLatest(null);
    setLog([]);
  }, [testMode]);

  // handleAudioReady — called by AudioRecorder with each VAD-gated utterance (Keyword Detection).
  const handleAudioReady = useCallback(async (wavBlob) => {
    const form = new FormData();
    form.append('audio', wavBlob, 'utterance.wav');

    return fetch(`${getApiBaseUrl()}/api/fsl/detect`, {
      method: 'POST',
      body: form,
      credentials: 'include',
    })
      .then(r => r.json())
      .then(data => {
        if (!data.success) return;

        const entry = {
          id: Date.now(),
          transcript: data.transcript || '(no speech)',
          triggered: data.triggered,
          keyword: data.top_keyword,
          score: data.top_score ?? 0,
          allScores: data.results ?? {},
          time: new Date().toLocaleTimeString(),
        };
        setLatest(entry);
        setLog(prev => [entry, ...prev].slice(0, MAX_LOG));
      })
      .catch(err => console.error('[ApexDemo] detect error:', err));
  }, []);

  // handleTranscriptionReady — called by AudioRecorder for Open Transcription.
  const handleTranscriptionReady = useCallback(async (wavBlob) => {
    if (wavBlob.size === 0) return;

    const form = new FormData();
    form.append('audio', wavBlob, 'utterance.wav');

    return fetch(`${getApiBaseUrl()}/api/fsl/detect`, {
      method: 'POST',
      body: form,
      credentials: 'include',
    })
      .then(r => r.json())
      .then(data => {
        const transcript = data.corrected_text || data.transcript || '';
        if (!data.success || !transcript.trim()) return;

        const entry = {
          id: Date.now(),
          transcript,
          time: new Date().toLocaleTimeString(),
        };
        setLatest(entry);
        setLog(prev => [entry, ...prev].slice(0, MAX_LOG));
      })
      .catch(err => console.error('[ApexDemo] transcribe error:', err));
  }, []);

  const handleSwitchMode = (mode) => {
    setLatest(null);
    setLog([]);
    setTestMode(mode);
  };

  async function handleRetrain() {
    // Reset backend session before returning to train phase
    await fetch(`${getApiBaseUrl()}/api/fsl/reset`, {
      method: 'POST', credentials: 'include'
    }).catch(() => { });
    onRetrain();
  }

  return (
    <div className="phase-wrap">
      <div className="phase-header">
        <span className="demo-badge">Step 2 of 2</span>
        <p className="phase-subtitle">
          Choose between keyword detection or open-ended speech transcription.
        </p>
      </div>

      <div className="demo-mode-selector">
        <button
          className={`mode-btn ${testMode === 'detection' ? 'active' : ''}`}
          onClick={() => handleSwitchMode('detection')}
        >
          Keyword Detection
        </button>
        <button
          className={`mode-btn ${testMode === 'transcription' ? 'active' : ''}`}
          onClick={() => handleSwitchMode('transcription')}
        >
          Open Transcription
        </button>
      </div>

      <div className="test-single">
        <div className="demo-card test-card">

          {/* Header */}
          {testMode === 'detection' ? (
            <>
              <h2>Live detection</h2>
              <p className="panel-desc" style={{ marginBottom: '0.5rem' }}>
                Say <strong>{keyword}</strong> to trigger detection, or say a different word to test non-matches.
              </p>
              <p className="panel-hint" style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', opacity: 0.85, marginBottom: '1.5rem', lineHeight: '1.4' }}>
                <strong>Note:</strong> The first detection may take a few seconds to initialize. Subsequent detections will be much faster.
              </p>
            </>
          ) : (
            <>
              <h2>Open transcription</h2>
              <p className="panel-desc">
                Speak freely and see the live transcription. Include <strong>{keyword}</strong> anywhere in your speech to test detection.
              </p>
            </>
          )}

          {/* Mic */}
          <div className="console-test-actions">
            <AudioRecorder
              onAudioReady={testMode === 'detection' ? handleAudioReady : handleTranscriptionReady}
              isLoading={false}
              onListeningChange={setIsListening}
            />
          </div>

          {/* Latest result */}
          {testMode === 'detection' ? (
            <div className={[
              'result-card',
              latest === null ? 'idle' : latest.triggered ? 'triggered' : 'ignored'
            ].join(' ')}>
              {latest === null ? (
                <div className="result-idle">
                  <span>Waiting for first utterance…</span>
                </div>
              ) : (
                <>
                  <div className="result-verdict">
                    {latest.triggered
                      ? <><span className="verdict-icon">✓</span> DETECTED</>
                      : <><span className="verdict-icon">✕</span> NOT DETECTED</>
                    }
                  </div>
                  <div className="result-transcript">"{latest.transcript}"</div>
                  <div className="score-bars">
                    {Object.entries(latest.allScores || {}).map(([kw, info]) => (
                      <div className="score-bar-row" key={kw}>
                        <span className="score-kw">{kw}</span>
                        <div className="score-track">
                          <div
                            className={`score-fill${info.detected ? ' detected' : ''}`}
                            style={{ width: `${((info.confidence ?? 0) * 100).toFixed(1)}%` }}
                          />
                        </div>
                        <span className="score-num">{(info.confidence ?? 0).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className={`result-card ${latest === null ? 'idle' : 'triggered'}`}>
              {latest === null ? (
                <div className="result-idle">
                  <span>Waiting for speech…</span>
                </div>
              ) : (
                <>
                  <div className="result-verdict" style={{ color: '#2FC4B0' }}>
                    <span className="verdict-icon">✓</span> TRANSCRIPTION
                  </div>
                  <div className="result-transcript" style={{ fontSize: '1.1rem', fontWeight: 500, color: 'var(--text-primary)' }}>
                    "{latest.transcript}"
                  </div>
                </>
              )}
            </div>
          )}

          {/* Inline scrollable log */}
          <div className="inline-log">
            <div className="log-header">
              <span className="log-header-label">
                {testMode === 'detection' ? 'Detection log' : 'Transcription log'}
              </span>
              {log.length > 0 && (
                <button className="btn-clear" onClick={() => { setLog([]); setLatest(null); }}>
                  Clear
                </button>
              )}
            </div>
            <div className="log-scroll">
              {log.length === 0 ? (
                <div className="log-empty">No entries yet — start speaking.</div>
              ) : (
                log.map(entry => (
                  <div
                    key={entry.id}
                    className={`log-entry${testMode === 'detection' && entry.triggered ? ' log-triggered' : ''}`}
                  >
                    <div className="log-entry-top">
                      {testMode === 'detection' ? (
                        <>
                          <span className={`log-badge${entry.triggered ? ' hit' : ' miss'}`}>
                            {entry.triggered ? `✓ ${entry.keyword}` : '✕ no match'}
                          </span>
                          <span className="log-score">{(entry.score ?? 0).toFixed(2)}</span>
                        </>
                      ) : (
                        <span className="log-badge hit">Transcript</span>
                      )}
                      <span className="log-time" style={{ marginLeft: 'auto' }}>
                        {entry.time}
                      </span>
                    </div>
                    <div className="log-transcript">"{entry.transcript}"</div>
                  </div>
                ))
              )}
            </div>
          </div>

          <button className="retrain-btn" onClick={handleRetrain}>
            ← Retrain with new keyword
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Root component ───────────────────────────────────────────────────────────
export default function ApexDemo() {
  const [phase, setPhase] = useState('train');  // 'train' | 'test'
  const [keyword, setKeyword] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
    // Warm up FSL status and standard ASR transcriber
    fetch(`${getApiBaseUrl()}/api/fsl/status`, { credentials: 'include' }).catch(() => { });
    fetch(`${getApiBaseUrl()}/api/warmup`, { method: 'POST', credentials: 'include' }).catch(() => { });
  }, []);

  function handleTrained(kw) {
    setKeyword(kw);
    setPhase('test');
    window.scrollTo(0, 0);
  }

  function handleRetrain() {
    setPhase('train');
    setKeyword('');
    window.scrollTo(0, 0);
  }

  return (
    <div className="apex-demo-container">
      <div className="demo-header-section">
        <h1>Apex Interactive Demo</h1>
      </div>

      {phase === 'train'
        ? <TrainPhase onTrained={handleTrained} />
        : <TestPhase keyword={keyword} onRetrain={handleRetrain} />
      }
    </div>
  );
}